import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HelpIcon from "@mui/icons-material/Help";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import Chip, { ChipProps } from "@mui/material/Chip";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { CompletedAppointmentIcon } from "components/icons";
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_READY,
  APPOINTMENT_STATUS_RESCHEDULED,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import { NEEDS_RESCHEDULE_FEATURE } from "constants/feature-flags";

type AppointmentStatusProps = {
  statusCode: number;
  size?: "small" | "medium";
  sx?: SxProps;
  disableLabelOnSmallScreen?: boolean;
  showOnlyIcon?: boolean;
  showLabel?: boolean;
  iconSx?: SxProps;
  needsReschedule?: boolean;
};

export default function AppointmentStatus({
  statusCode,
  size = "medium",
  sx,
  needsReschedule,
  disableLabelOnSmallScreen,
  showOnlyIcon,
  showLabel = true,
  iconSx = {}
}: AppointmentStatusProps) {
  const flags = useFlags();
  const needsRescheduleFeature = flags[NEEDS_RESCHEDULE_FEATURE];

  const { breakpoints } = useTheme();
  const isLargeScreen = useMediaQuery(breakpoints.up("xl"));

  const smallScreenStyles = disableLabelOnSmallScreen &&
    !isLargeScreen && {
      padding: 0,
      height: "auto",
      borderRadius: "50%",
      "& .MuiChip-label": { padding: 0 },
      "& .MuiChip-icon": {
        margin: 0
      }
    };

  const sxStyles = { ...sx, ...smallScreenStyles };

  const getLabel = useCallback(
    (index: number) => {
      if (disableLabelOnSmallScreen && !isLargeScreen) return null;
      return APPOINTMENT_STATUSES.getFromCode(index);
    },
    [disableLabelOnSmallScreen, isLargeScreen]
  );

  const statusDetails: Record<number, ChipProps> = {
    [APPOINTMENT_STATUS_DRAFT]: {
      icon: <InsertInvitationIcon fontSize="small" sx={iconSx} />,
      label: getLabel(0),
      color: "default",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_UNCONFIRMED]: {
      icon: <HelpIcon fontSize="small" sx={iconSx} />,
      label: getLabel(1),
      color: "default",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_CONFIRMED]: {
      icon: <EventAvailableIcon fontSize="small" sx={iconSx} />,
      label: getLabel(2),
      color: "success",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_CANCELED]: {
      icon: <CancelIcon fontSize="small" sx={iconSx} />,
      label: getLabel(3),
      color: "error",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_NO_SHOW]: {
      icon: <VisibilityOffIcon fontSize="small" sx={iconSx} />,
      label: getLabel(4),
      color: "error",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_RESCHEDULED]: {
      icon: <EditCalendarIcon fontSize="small" sx={iconSx} />,
      label: getLabel(7),
      color: "error",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_ARRIVED]: {
      icon: <PersonPinCircleIcon fontSize="small" sx={iconSx} />,
      label: getLabel(5),
      color: "info",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_READY]: {
      icon: <WhereToVoteIcon fontSize="small" sx={iconSx} />,
      label: getLabel(6),
      color: "primary",
      size,
      sx: sxStyles
    },
    [APPOINTMENT_STATUS_COMPLETED]: {
      icon: (
        <CompletedAppointmentIcon
          style={{
            padding: "2px",
            maxHeight: "1rem",
            maxWidth: "1rem",
            marginLeft: "-1px",
            fill: "#fff",
            ...(iconSx as any)
          }}
        />
      ),
      label: getLabel(99),
      color: "success",
      size,
      sx: sxStyles
    }
  };

  if (showOnlyIcon && needsReschedule && needsRescheduleFeature) {
    return <ManageHistoryIcon fontSize="small" sx={iconSx} />;
  }
  if (showOnlyIcon) return statusDetails[statusCode].icon ?? null;

  const extProps: ChipProps =
    needsReschedule && needsRescheduleFeature
      ? {
          ...statusDetails[statusCode],
          icon: <ManageHistoryIcon fontSize="small" sx={iconSx} />,
          color: "error"
        }
      : statusDetails[statusCode];

  return (
    <Chip
      {...extProps}
      label={showLabel ? statusDetails[statusCode].label : null}
    />
  );
}
